import React from 'react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import { Box, Text, VStack } from '@chakra-ui/react';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.tsx';
import { PrimaryCheckbox } from '@/components/ui/checkbox';
import { displayRecordName } from '@/utils/documentUtils';

interface DocumentPushConfirmationDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: () => void;
	onCancel: () => void;
	isLoading?: boolean;
	pendingDocuments?: Document[];
	selectedDocumentIds?: Set<number>;
	onDocumentSelectedChange?: (documentId: number, selected: boolean) => void;
}

/**
 * Confirmation dialog for choosing which records to send to the current owner.
 * Uses the existing ConfirmationDialog component to maintain consistency.
 */
const DocumentPushConfirmationDialog: React.FC<DocumentPushConfirmationDialogProps> = ({
	isOpen,
	onOpenChange,
	onConfirm,
	onCancel,
	isLoading = false,
	pendingDocuments = [],
	selectedDocumentIds = new Set(),
	onDocumentSelectedChange,
}) => {
	const hasPendingDocs = pendingDocuments.length > 0;

	const message = hasPendingDocs
		? pendingDocuments.length === 1
			? "Select the record to send to the current owner's Bundle:"
			: "Select records to send to the current owner's Bundle:"
		: "Send this update to the current owner's Bundle?";

	return (
		<ConfirmationDialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={pendingDocuments.length > 1 ? 'Send records?' : 'Send record?'}
			message={message}
			confirmText="Send"
			cancelText="Cancel"
			onConfirm={onConfirm}
			onCancel={onCancel}
			isLoading={isLoading}
			loadingText="Sending..."
			confirmButtonVariant="solid"
			confirmButtonColorPalette="brand"
			disabled={hasPendingDocs && selectedDocumentIds.size === 0}
		>
			{hasPendingDocs && (
				<VStack
					align="stretch"
					gap={2}
					p={4}
					mt={4}
					bg="gray.50"
					borderRadius="lg"
					maxH="200px"
					overflowY="auto"
				>
					{pendingDocuments.map((doc) => (
						<PrimaryCheckbox
							key={doc.id}
							checked={selectedDocumentIds.has(doc.id)}
							onCheckedChange={(details) => onDocumentSelectedChange?.(doc.id, details.checked === true)}
							controlProps={{ borderRadius: 'md' }}
							alignItems="flex-start"
							gap={3}
						>
							<Box minW={0} flex={1}>
								<Text
									textStyle="label"
									lineHeight="1.4"
									lineClamp={2}
									wordBreak="break-word"
									overflowWrap="anywhere"
								>
									{displayRecordName(doc) || `Document #${doc.id}`}
								</Text>
							</Box>
						</PrimaryCheckbox>
					))}
				</VStack>
			)}
		</ConfirmationDialog>
	);
};

export default DocumentPushConfirmationDialog;
